/* eslint-disable @nrwl/nx/enforce-module-boundaries */
/**
 * 名片相关
 */
import { defineStore } from 'pinia';
import { Toast } from 'vant';
import { businessCardApi, commonApi } from '@shared/apis';
import { useUserStore, useGlobalStore, useThemeStore } from '@shared/store';
import { getNetworkImage, circleImg } from '../../utils';
import type {
  IHeaderCardType,
  IPageCardType,
  StationGetStationNameRes,
  IPosterCardType,
  IPageCardThemeType,
} from '@shared/types/businessCard';
import { MANAGER_CARD_TYPE, CERTIFICATE_SHOW_TYPE } from '@shared/config';
import Color from 'color';
import { searchToJson } from '@shared/utils';

type StateType = {
  headerCardInfo: IHeaderCardType;
  pageCardInfo: IPageCardType;
  posterCardInfo: IPosterCardType;
  drawQrCodeUrl: string;
  pageCardTheme: IPageCardThemeType;
};

export const useBusinessCardStore = defineStore('businessCard', {
  state: (): StateType => {
    return {
      headerCardInfo: {} as IHeaderCardType,
      pageCardInfo: {} as IPageCardType,
      posterCardInfo: {} as IPosterCardType,
      drawQrCodeUrl: '',
      pageCardTheme: {} as IPageCardThemeType,
    };
  },
  actions: {
    async getCardInfo(cardType: MANAGER_CARD_TYPE = MANAGER_CARD_TYPE.HeaderCard) {
      try {
        const userStore = useUserStore();
        const res = await businessCardApi.getBusinessCardInfoApi({
          cardType: cardType.toString(),
          staffId: userStore.viewStaffInfo?.staffId || searchToJson()?.staffId || '',
        });
        const cardInfo = res.retdata;
        const info: any = {
          name: cardInfo?.name || '',
          avatar: cardInfo?.avatar || 'https://webcdn.tengmoney.com/usm/FrRzwHbhRUyKQ07dgJ_zzCjQhmnU-92-92.png',
          corpName: cardInfo?.showCorpName ? cardInfo.corpName || '' : '',
          deptList:
            cardInfo?.showDept && cardInfo.deptConfigResDTOList
              ? cardInfo.deptConfigResDTOList.filter((item) => item.showDept).map((deptItem) => deptItem.deptName || '')
              : [],
          // dept: managerInfo.deptInHead && managerInfo.dept ? managerInfo.dept : '',
          position: cardInfo?.showPosition ? cardInfo.position || '' : '',
          mobile: cardInfo?.showMobile && cardInfo.mobile ? cardInfo.mobile : '',
          staffId: userStore.viewStaffInfo?.staffId,
          // 页面名片不受showStudio控制,因为本身不展示云工作室icon, 只有早报的机构头图版的名片展示了云工作室入口，后面有自己的展示逻辑
          studio: cardInfo?.showStudio ? '小站' : '',
          contact: cardInfo?.showContact ? '微信' : '',
          certificateList: cardInfo?.showCertificate ? userStore.viewStaffInfo?.certificateList : [], // 证书列表暂时没有微服务的接口 用经理信息获取相关字段
        };

        if (cardType == MANAGER_CARD_TYPE.PageCard) {
          info.address = cardInfo.showAddress ? cardInfo.address : '';
          info.desc = cardInfo.showDesc ? cardInfo.desc : '';
          info.contact = cardInfo.showContact ? '微信' : '';
          info.tagList = cardInfo.showTag && cardInfo.tagList ? cardInfo.tagList : [];
          info.selfBigLogo = userStore.viewStaffInfo.corpSelfUploadLogoUrl;
          info.selfSmallLogo = userStore.viewStaffInfo.corpSelfUploadSmallLogoUrl;
          info.corpLogo = userStore.viewStaffInfo.fontColor
            ? userStore.viewStaffInfo.corpCompleteLogoUrl
            : userStore.viewStaffInfo.corpWhiteCompleteLogoUrl;
          info.corpCompleteLogoUrl = userStore.viewStaffInfo.corpCompleteLogoUrl;
        }

        if (!cardInfo?.showStudio && cardType == MANAGER_CARD_TYPE.HeaderCard) {
          this.headerCardInfo = info;
          return;
        }

        /**
         * 早报机构头图版名片用到的云工作室名字
         */
        const response = await commonApi.getStationNameApi<StationGetStationNameRes>();
        if (response.ret === 0 && response.retdata?.customize) {
          const { customize, replaceCustomize = '' } = response.retdata;
          const managerNamePlaceHolder = replaceCustomize?.replace(customize, '');
          info.studio = managerNamePlaceHolder
            ? replaceCustomize?.replace(managerNamePlaceHolder, info.name || '')
            : replaceCustomize;
          // info.stationName = response.retdata.customize;
        }
        switch (cardType) {
          case MANAGER_CARD_TYPE.HeaderCard:
            this.headerCardInfo = info;
            break;
          case MANAGER_CARD_TYPE.PageCard:
            this.pageCardInfo = info;
            break;
        }
      } catch (error) {
        console.error('%c getPosterCategoryListApi error:', 'color: #fff;background: #b457ff;', error);
      }
    },
    async drawQrCode() {
      if (this.drawQrCodeUrl) {
        return this.drawQrCodeUrl;
      }
      const userStore = useUserStore();
      const globalStore = useGlobalStore();
      const { width, height } = { width: 315, height: 376 };
      const info = userStore.viewStaffInfo;
      const isManager = globalStore.isManagerView;
      const dpr = window.devicePixelRatio || 1;
      const canvas = document.createElement('canvas');
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = width + 'px';
      canvas.style.height = height + 'px';
      const context = canvas.getContext('2d');
      context?.scale(dpr, dpr);

      try {
        Toast({
          message: '二维码正在生成',
          duration: 60 * 1000,
          overlay: true,
        });
        const dpr = window.devicePixelRatio || 1;
        const originWidth = canvas.width / dpr;
        const originHeight = canvas.height / dpr;
        const context = canvas.getContext('2d')!;
        context.fillStyle = '#FFFFFF';
        context.fillRect(0, 0, canvas.width, canvas.height);
        if (!info.qrCode) {
          Toast('暂无经理二维码信息');
          return false;
        }
        const qrCodeImg = await getNetworkImage(info.qrCode!);
        const avatarImg = await getNetworkImage(info.avatar!);
        circleImg(context, avatarImg, originWidth * 0.0465, originHeight * 0.05, originWidth * 0.068);

        context.drawImage(qrCodeImg, originWidth * 0.05, originHeight * 0.17, originWidth * 0.9, originWidth * 0.9);

        context.font = `${originWidth * 0.055}px 黑体`;
        context.fillStyle = '#111111';
        const maxNameLength = Math.floor((originWidth - originWidth * 0.21) / (originWidth * 0.055));
        if (info.name!.length > maxNameLength) {
          context.fillText(info.name!.slice(0, maxNameLength - 3) + '...', originWidth * 0.21, originHeight * 0.1);
        } else {
          context.fillText(info.name!, originWidth * 0.21, originHeight * 0.1);
        }

        context.font = `${originWidth * 0.037}px 黑体`;
        context.fillStyle = '#999999';
        const maxLength = Math.floor((originWidth - originWidth * 0.21) / (originWidth * 0.037));
        if (info.corpName!.length > maxLength) {
          const cropName = info.corpName!.slice(0, maxLength - 3) + '...';
          context.fillText(cropName, originWidth * 0.21, originHeight * 0.15);
        } else {
          context.fillText(info.corpName!, originWidth * 0.21, originHeight * 0.15);
        }

        context.font = `${originWidth * 0.043}px 黑体`;
        context.fillStyle = '#666666';
        context.textAlign = 'center';
        if (isManager) {
          context.fillText('客户可以通过扫描二维码添加或者联系您', originWidth / 2, originHeight * 0.97);
        } else {
          context.fillText('请长按二维码添加或联系客户经理', originWidth / 2, originHeight * 0.97);
        }
        this.drawQrCodeUrl = canvas.toDataURL();
        Toast.clear();
      } catch (error) {
        Toast.clear();
      }
    },
    async getPosterCardInfo() {
      try {
        const userStore = useUserStore();
        const res = await businessCardApi.getBusinessCardInfoApi({
          cardType: MANAGER_CARD_TYPE.PosterCard.toString(),
          staffId: userStore.viewStaffInfo?.staffId || searchToJson()?.staffId || '',
        });
        const posterCardInfo = res.retdata!;

        const info: IPosterCardType = {
          name: posterCardInfo.name,
          avatar: posterCardInfo.avatar || 'https://webcdn.tengmoney.com/usm/FrRzwHbhRUyKQ07dgJ_zzCjQhmnU-92-92.png',
          corpName: posterCardInfo.showCorpName ? posterCardInfo.corpName : '',
          deptList:
            posterCardInfo.showDept && posterCardInfo.deptConfigResDTOList
              ? posterCardInfo.deptConfigResDTOList
                  .filter((item) => item.showDept)
                  .map((deptItem) => deptItem.deptName!)
              : [],
          position: posterCardInfo.showPosition ? posterCardInfo.position : '',
          mobile: posterCardInfo.showMobile && posterCardInfo.mobile ? posterCardInfo.mobile : '',
          address: posterCardInfo.showAddress ? posterCardInfo.address : '',
          certificateList: posterCardInfo.showCertificate ? userStore.viewStaffInfo.certificateList! : [],
          qrCodeUrl: posterCardInfo.showQrCode ? posterCardInfo.qrCodeUrl : '',
          certificateShowType:
            posterCardInfo.certificateShowType === CERTIFICATE_SHOW_TYPE.PersonalCertificate
              ? CERTIFICATE_SHOW_TYPE.PersonalCertificate
              : CERTIFICATE_SHOW_TYPE.GroupCertificate,
          certGroup: [],
          desc: posterCardInfo.showDesc ? posterCardInfo.desc : '',
        };

        if (
          !posterCardInfo.showCertificate ||
          posterCardInfo.certificateShowType === CERTIFICATE_SHOW_TYPE.PersonalCertificate
        ) {
          this.posterCardInfo = info;
          return;
        }
        const response = await businessCardApi.getCertificateGroup({
          staffId: userStore.viewStaffInfo?.staffId || searchToJson()?.staffId || '',
        });
        if (response.ret === 0 && response.retdata) {
          const certGroup = response.retdata;
          info.certGroup = certGroup;
          this.posterCardInfo = info;
        }
      } catch (error) {
        console.error(error);
      }
    },
    async getPageCardThemeAction() {
      const themeStore = useThemeStore();
      const userStore = useUserStore();

      const managerInfo = userStore.viewStaffInfo;
      const theme = {
        mainColor: themeStore.stationBgColor.mainBgColor,
        isSetFontColor: !!managerInfo.fontColor,
      };
      const iconColor = managerInfo.fontColor ? (managerInfo.mainColorStyle ? theme.mainColor : '#111111') : '#ffffff';
      const tagColor = Color(iconColor)
        .alpha(!managerInfo.mainColorStyle && managerInfo.fontColor ? 1 : 0.15)
        .rgb()
        .toString();
      this.pageCardTheme = {
        backgroundColor: managerInfo.mainColorStyle
          ? 'linear-gradient(to top left, rgb(213, 213, 213), rgb(219, 219, 219))'
          : theme.mainColor,
        backgroundColor2: managerInfo.mainColorStyle ? '#ffffff' : theme.mainColor,
        iconColor,
        color: managerInfo.fontColor ? '#111111' : '#ffffff',
        backgroundImage: managerInfo.corpSelfUploadLogoUrl || '',
        tagColor,
        tagFontColor: managerInfo.fontColor ? theme.mainColor : '#ffffff',
        mainColor: theme.mainColor,
        isSetFontColor: theme.isSetFontColor,
      };
    },
  },
});
